import { useContext, useEffect, useState } from "react";
import parse from "html-react-parser";
import TitleBar from "../components/layout/TitleBar";
import TabBar from "../components/ui/TabBar";
import Tab from "../components/ui/Tab";
import { Link } from "react-router-dom";
import Collapsible from "react-collapsible";
import chevronDownIcon from "../assets/icons/chevron-down.svg";
import chevronUpIcon from "../assets/icons/chevron-up-double.svg";
import SEO from "../components/shared/SEO";
import { useGetCacheContent } from "../hooks/useGetCacheContent";
import { scrollToTop } from "../../src/utilities/scrolltoTop";
import poweredByContext from "../store/PoweredByContext";
import LazyLoadingContext from "../store/LazyLoadingContext";
import { getUrl } from "../utilities/url";
import getClassName from "../utilities/getClassName";

const MovidaTermsConditions = () => {
    // State
    const [expandable, setExpandable] = useState(true);
    const [allCollapsiblesOpen, setAllCollapsiblesOpen] = useState([]);

    // Get and cache content
    const { content: termsConditionsContent } = useGetCacheContent(
        "movidaTermsConditionsContent",
        "/api/frontend/get-movida-terms",
        500
    );

    const poweredByCtx = useContext(poweredByContext);
    const lazyLoadingCtx = useContext(LazyLoadingContext);

    useEffect(() => {
        return () => {
            lazyLoadingCtx.reset("movidaTermsConditions");
        };
        // Intentionally mount-only: re-firing would call reset() and clobber
        // page-ready state that other effects have already populated.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        lazyLoadingCtx.updatePageCheck("movidaTermsConditions", {
            termsConditionsContent: termsConditionsContent !== null,
        });
    }, [termsConditionsContent, lazyLoadingCtx]);

    useEffect(() => {
        let items = [];
        if (termsConditionsContent) {
            termsConditionsContent.terms.forEach((item, i) => {
                items.push(false);
            });
        }

        setAllCollapsiblesOpen(items);
    }, [termsConditionsContent]);

    useEffect(() => {
        if (allCollapsiblesOpen.length > 0) {
            let valid = true;

            if (expandable) {
                allCollapsiblesOpen.forEach((item) => {
                    if (item === false) {
                        valid = false;
                    }
                });
                if (valid) {
                    setExpandable(false);
                }
            } else {
                allCollapsiblesOpen.forEach((item) => {
                    if (item === true) {
                        valid = false;
                    }
                });
                if (valid) {
                    setExpandable(true);
                }
            }
        }
    }, [allCollapsiblesOpen, expandable]);

    const changeAll = () => {
        if (expandable) {
            openAll();
        } else {
            closeAll();
        }
    };

    const openAll = () => {
        let items = [];

        JSON.parse(JSON.stringify(allCollapsiblesOpen)).forEach((item, i) => {
            if (!item) {
                item = true;
            }
            items.push(true);
        });

        setAllCollapsiblesOpen(items);
        setExpandable(false);
    };

    const closeAll = () => {
        let items = [];

        JSON.parse(JSON.stringify(allCollapsiblesOpen)).forEach((item, i) => {
            if (!item) {
                item = false;
            }
            items.push(false);
        });

        setAllCollapsiblesOpen(items);
        setExpandable(true);
    };

    const updateCollapsible = (i, open) => {
        let items = JSON.parse(JSON.stringify(allCollapsiblesOpen));
        items[i] = open;

        setAllCollapsiblesOpen(items);
    };

    return (
        <>
            {termsConditionsContent && (
                <SEO
                    title={termsConditionsContent.title}
                    description={termsConditionsContent.description}
                    canonicalUrl={getUrl(poweredByCtx, window.location.pathname)}
                />
            )}

            <TitleBar title="Terms & Privacy Policy" cta="Home" url="/" />

            <TabBar>
                <Link to={`/privacy-policy`}>
                    <Tab selected={false}>Privacy Policy</Tab>
                </Link>
                <Link to={`/terms-conditions`}>
                    <Tab selected={false}>Terms & Conditions</Tab>
                </Link>
                <Link to={`/movida-terms-conditions`}>
                    <Tab selected={true}>Movida Terms & Conditions</Tab>
                </Link>
            </TabBar>
            <div className="page">
                <div className="wrap wrap--1040">
                    <div
                        className={getClassName([
                            "collapsible-content",
                            lazyLoadingCtx.pageReady && termsConditionsContent ? "" : "collapsible-content--skeleton",
                        ])}
                    >
                        <div className="collapsible-content__header">
                            <div className="collapsible-content__intro">
                                <p>
                                    Your Movida terms & conditions are outlined below. Please read through thoroughly
                                    your terms and conditions.
                                </p>
                            </div>
                            <button className="collapsible-content__toggle" onClick={changeAll}>
                                {expandable ? "Expand all" : "Collapse all"} <img src={chevronDownIcon} alt="" />
                            </button>
                        </div>
                        {lazyLoadingCtx.pageReady && termsConditionsContent ? (
                            <div className="collapsible-content__items">
                                {termsConditionsContent.terms.map((item, i) => {
                                    return (
                                        <Collapsible
                                            open={allCollapsiblesOpen[i]}
                                            onTriggerOpening={updateCollapsible.bind(this, i, true)}
                                            onTriggerClosing={updateCollapsible.bind(this, i, false)}
                                            trigger={`${item.order}. ${item.sectionTitle}`}
                                            key={i}
                                        >
                                            {parse(item.terms)}
                                        </Collapsible>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="collapsible-content__items">
                                <Collapsible />
                                <Collapsible />
                                <Collapsible />
                                <Collapsible />
                                <Collapsible />
                            </div>
                        )}
                        <div className="collapsible-content__footer">
                            <button className="collapsible-content__to-top" onClick={scrollToTop}>
                                <img src={chevronUpIcon} alt="" /> Back to top
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default MovidaTermsConditions;
